import { defineStore } from 'pinia'

import {
  FIRST_ONBOARDING_STEP,
  type OnboardingStepKey,
  type WorkspaceBrandDefaults,
} from '../models/onboarding.types'
import {
  clearOnboardingSession,
  readOnboardingSession,
  writeOnboardingSession,
  type OnboardingMode,
} from '../service/onboardingSession'
import {
  canRevisit as canRevisitStep,
  normalizeOnboardingStep,
  stepNumber,
} from '../service/onboardingSteps'

// Contract 29 §7 — the onboarding slice of the retired `layoutStore`, now also the takeover's own
// state: v3's `onboardingModal` / `onboardingStep` pair (`layoutStore.ts:109-148, 500-531`).
//
// The wizard is a full-screen TAKEOVER over the app, not a route (product decision, 2026-10-22):
// it has to appear on its own conditions and the user must not be able to navigate around it. So
// the open flag and the current step live here; only the position is mirrored to `sessionStorage`
// so an OAuth round trip from the connect step resumes where it left (`onboardingSession.ts`).
export const useOnboardingStore = defineStore('onboarding', {
  state: () => ({
    /** §6.2's response — the step-2 seed (was `workspaceStore.setOnboardingDefaults`). */
    defaults: null as WorkspaceBrandDefaults | null,
    /** §6.1's response. */
    createdWorkspaceId: null as number | null,
    /** 1..4 — the "you may go back, never forward" clamp. */
    furthestStep: 1,
    /** The takeover is showing (v3 `onboardingModal`). */
    open: false,
    /** `required` cannot be dismissed; `optional` (a new workspace) can, from the logo. */
    mode: 'optional' as OnboardingMode,
    /** The step on screen (v3 `onboardingStep`). */
    step: FIRST_ONBOARDING_STEP as OnboardingStepKey,
  }),
  getters: {
    /** Ports `layoutStore.setOnboardStep`'s clamp (`layoutStore.ts:530-532`). */
    canRevisit(state) {
      return (step: OnboardingStepKey): boolean => canRevisitStep(step, state.furthestStep)
    },
  },
  actions: {
    setDefaults(defaults: WorkspaceBrandDefaults | null): void {
      this.defaults = defaults
    },
    setCreatedWorkspace(id: number | null): void {
      this.createdWorkspaceId = id
    },
    /** Monotonic: reaching a step can only ever unlock it, never re-lock a completed one. */
    advanceTo(step: OnboardingStepKey): void {
      this.furthestStep = Math.max(this.furthestStep, stepNumber(step))
    },
    /** v3 `activeOnboard()` (+ `setOnboardStep(n, true)` for the Shopify start). */
    start(options: { mode: OnboardingMode; step?: OnboardingStepKey }): void {
      const step = normalizeOnboardingStep(options.step ?? FIRST_ONBOARDING_STEP)
      this.open = true
      this.mode = options.mode
      this.step = step
      this.furthestStep = stepNumber(step)
      writeOnboardingSession({ step, mode: this.mode })
    },
    /** A mandatory condition outranks an optional run already on screen — it can no longer close. */
    require(): void {
      this.mode = 'required'
      if (this.open) writeOnboardingSession({ step: this.step, mode: this.mode })
    },
    setStep(step: OnboardingStepKey): void {
      const normalized = normalizeOnboardingStep(step)
      this.step = normalized
      this.advanceTo(normalized)
      if (this.open) writeOnboardingSession({ step: normalized, mode: this.mode })
    },
    /** Reopen the run an OAuth round trip interrupted. `false` when there is none. */
    resume(): boolean {
      const record = readOnboardingSession()
      if (!record) return false
      this.start(record)
      return true
    },
    /** v3 `disableOnboard()`: the takeover goes, and its seed and position with it. */
    close(): void {
      this.open = false
      this.mode = 'optional'
      this.step = FIRST_ONBOARDING_STEP
      clearOnboardingSession()
      this.reset()
    },
    reset(): void {
      this.defaults = null
      this.createdWorkspaceId = null
      this.furthestStep = 1
    },
  },
})

export type OnboardingStore = ReturnType<typeof useOnboardingStore>
